import React, { useState, useEffect } from 'react';
import axios from 'axios';
import confetti from 'canvas-confetti';

// Get the API URL from environment variable or fallback to localhost for development
const API_URL = process.env.REACT_APP_API_URL ||
                (window.location.hostname === 'localhost'
                  ? 'http://localhost:5000'
                  : 'https://isdestichtinggezond-nl.onrender.com');

const SavingsChecker = ({ onClose }) => {
  const [memberNumber, setMemberNumber] = useState('');
  const [savingsData, setSavingsData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showCelebration, setShowCelebration] = useState(false);
  const [hasSeenCelebration, setHasSeenCelebration] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!memberNumber.trim()) {
      setError('Vul je lidnummer in.');
      return;
    }

    setIsLoading(true);
    setError(null);
    setSavingsData(null);

    try {
      const response = await axios.get(`${API_URL}/api/savings/${memberNumber.trim()}`);
      setSavingsData(response.data);
    } catch (err) {
      console.error('Error fetching savings:', err);
      if (err.response && err.response.status === 404) {
        setError(err.response.data.error || 'Lidnummer niet gevonden. Controleer je nummer of neem contact op.');
      } else {
        setError('Er is een fout opgetreden. Probeer het later opnieuw.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleReset = () => {
    setMemberNumber('');
    setSavingsData(null);
    setError(null);
    setHasSeenCelebration(false);
    setShowCelebration(false);
  };

  const triggerConfettiSequence = () => {
    // Stage 1: Initial burst
    confetti({
      particleCount: 100,
      spread: 70,
      origin: { y: 0.6 },
      colors: ['#041c35', '#c9ae5d', '#ffffff', '#d4bd6f'],
      ticks: 300
    });

    // Stage 2: Side cannons
    setTimeout(() => {
      confetti({
        particleCount: 50,
        angle: 60,
        spread: 55,
        origin: { x: 0 },
        colors: ['#041c35', '#c9ae5d', '#ffffff']
      });
      confetti({
        particleCount: 50,
        angle: 120,
        spread: 55,
        origin: { x: 1 },
        colors: ['#041c35', '#c9ae5d', '#ffffff']
      });
    }, 300);

    // Stage 3: Gold shower
    setTimeout(() => {
      confetti({
        particleCount: 150,
        spread: 180,
        origin: { y: 0 },
        colors: ['#c9ae5d', '#d4bd6f'],
        gravity: 0.8,
        ticks: 400
      });
    }, 600);

    // Stage 4: Final sparkle burst
    setTimeout(() => {
      confetti({
        particleCount: 80,
        spread: 360,
        origin: { y: 0.5, x: 0.5 },
        colors: ['#c9ae5d', '#ffffff'],
        shapes: ['circle'],
        scalar: 1.2
      });
    }, 1200);
  };

  useEffect(() => {
    const goalReached = savingsData && (savingsData.progress >= 100 || savingsData.remaining <= 0 || savingsData.savings >= savingsData.goal);

    if (goalReached && !hasSeenCelebration) {
      setTimeout(() => {
        setShowCelebration(true);
        setHasSeenCelebration(true);
        triggerConfettiSequence();
      }, 500);
    }
  }, [savingsData, hasSeenCelebration]);

  return (
    <div className="savings-overlay">
      <div className="savings-modal">
        <button className="close-button" onClick={onClose}>×</button>

        {showCelebration && savingsData && (savingsData.progress >= 100 || savingsData.remaining <= 0 || savingsData.savings >= savingsData.goal) && (
          <div className="celebration-overlay">
            <div className="celebration-content">
              <img
                src="/celebration.jpeg"
                alt="Gefeliciteerd!"
                className="celebration-image"
              />
              <h3 className="celebration-message">
                Mega Grote Ckas!<br />
                Je hebt je spaardoel behaald! 🎉
              </h3>
              <div className="celebration-controls">
                <button
                  className="celebration-dismiss"
                  onClick={() => setShowCelebration(false)}
                >
                  Klaar nu
                </button>
                <button
                  className="celebration-replay"
                  onClick={triggerConfettiSequence}
                >
                  🎊 Nog een keer!
                </button>
              </div>
            </div>
          </div>
        )}

        <div className="savings-content">
          <h2>Lustrum Spaargeld</h2>
          <p className="savings-subtitle">Controleer je spaargeld voor het lustrum</p>

          {!savingsData ? (
            <form onSubmit={handleSubmit} className="savings-form">
              <div className="form-group">
                <label htmlFor="memberNumber">Lidnummer</label>
                <input
                  type="text"
                  id="memberNumber"
                  value={memberNumber}
                  onChange={(e) => setMemberNumber(e.target.value)}
                  placeholder="Vul je lidnummer in"
                  disabled={isLoading}
                  autoFocus
                />
              </div>

              {error && (
                <div className="error-message">
                  {error}
                </div>
              )}

              <button
                type="submit"
                className="submit-button"
                disabled={isLoading}
              >
                {isLoading ? 'Laden...' : 'Controleer Spaargeld'}
              </button>
            </form>
          ) : (
            <div className="savings-result">
              <div className="savings-info">
                <div className="info-row">
                  <span className="info-label">Lidnummer:</span>
                  <span className="info-value">{savingsData.memberNumber}</span>
                </div>
                <div className="info-row">
                  <span className="info-label">Huidig Spaargeld:</span>
                  <span className="info-value savings-amount">€{savingsData.savings.toFixed(2)}</span>
                </div>
                <div className="info-row">
                  <span className="info-label">Doel:</span>
                  <span className="info-value">€{savingsData.goal.toFixed(2)}</span>
                </div>
                <div className="info-row">
                  <span className="info-label">Nog te Sparen:</span>
                  <span className="info-value">€{savingsData.remaining.toFixed(2)}</span>
                </div>
                <div className="info-row">
                  <span className="info-label">Laatste update:</span>
                  <span className="info-value info-date">
                    {savingsData.lastUpdated ? (
                      new Date(savingsData.lastUpdated).toLocaleDateString('nl-NL', {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric'
                      })
                    ) : (
                      'Onbekend'
                    )}
                  </span>
                </div>
              </div>

              <div className="progress-container">
                <div className="progress-label">
                  <span>Voortgang</span>
                  <span className="progress-percentage">{savingsData.progress.toFixed(1)}%</span>
                </div>
                <div className="progress-bar">
                  <div
                    className="progress-fill"
                    style={{ width: `${savingsData.progress}%` }}
                  />
                </div>
              </div>

              {(savingsData.progress >= 100 || savingsData.remaining <= 0 || savingsData.savings >= savingsData.goal) && !showCelebration && (
                <button
                  className="replay-celebration-btn"
                  onClick={() => {
                    setShowCelebration(true);
                    triggerConfettiSequence();
                  }}
                >
                  🎉 Opnieuw
                </button>
              )}

              <button
                onClick={handleReset}
                className="reset-button"
              >
                Controleer Ander Lidnummer
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SavingsChecker;
